import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { useCallback, useRef, useState } from 'react'

import { Button } from '../button'
import { Command } from '../command'
import { Switch } from '../forms'
import { Check, ChevronUpDown, Drag } from '../icons'
import { Popover } from '../Popover'
import { SelectOption } from '../select'
import { classes } from '../utils'

type MultiSelectProps = {
  isMulti: true
  useSwitches?: boolean
  value: string[]
  onValueChange: (selected: string[]) => void
  formatSelected?: (selected: string[]) => React.ReactNode
}

type SingleSelectProps = {
  isMulti?: false
  useSwitches?: false
  value: string
  onValueChange: (selected: string) => void
  formatSelected?: (selected: string) => React.ReactNode
}

type SelectProps = MultiSelectProps | SingleSelectProps

// TODO: make this
// type ComboBoxOption = SelectOption & {
//   icon?: React.ReactNode
// }

type Props = {
  options: SelectOption[]
  placeholder?: string
  onRearrange?: (newOptions: SelectOption[]) => void
  onAddOption?: (option: SelectOption) => void
  popover?: Pick<
    React.ComponentProps<typeof Popover.Content>,
    'side' | 'align' | 'className' | 'portal'
  >
  width?: number
} & SelectProps

export function ComboBox({
  options,
  isMulti,
  useSwitches,
  value,
  onValueChange,
  placeholder,
  onRearrange,
  onAddOption,
  formatSelected,
  popover,
  width,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)

  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  )

  const renderSelected = useCallback(() => {
    if (formatSelected) {
      // @ts-expect-error: fixme please
      const formatted = formatSelected(value)
      return formatted || placeholder || 'Select an option'
    } else if (isMulti) {
      return `${value.length} selected`
    } else {
      return value || placeholder || 'Select an option'
    }
  }, [value, formatSelected, isMulti, placeholder])

  const handleChange = useCallback(
    (optValue: string) => {
      if (isMulti) {
        const selected = value.includes(optValue)
          ? value.filter((v) => v !== optValue)
          : value.concat(optValue)
        onValueChange(selected)
        if (onAddOption) {
          inputRef.current?.focus()
        }
      } else {
        onValueChange(optValue)
        setIsOpen(false)
      }
    },
    [value, onValueChange, isMulti, onAddOption],
  )

  const renderActiveDecoration = useCallback(
    (option: SelectOption) => {
      if (isMulti && useSwitches) {
        return (
          <Switch
            size="tiny"
            checked={value.includes(option.value)}
            onClick={() => handleChange(option.value)}
            disabled={option.disabled}
          />
        )
      } else {
        const isActive = isMulti
          ? value.includes(option.value)
          : value === option.value
        return (
          <Check
            className={classes(
              'h-4 w-4',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
        )
      }
    },
    [value, isMulti, handleChange, useSwitches],
  )

  const handleAddOption = useCallback(() => {
    if (!inputValue || !onAddOption) return
    onAddOption({ value: inputValue, label: inputValue })
    setInputValue('')
    inputRef.current?.focus()
  }, [inputValue, onAddOption])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!!over?.id && active.id !== over.id) {
        const oldIndex = options.findIndex((opt) => opt.value === active.id)
        const newIndex = options.findIndex((opt) => opt.value === over.id)
        const newOptions = arrayMove(options, oldIndex, newIndex)
        if (onRearrange) {
          onRearrange(newOptions)
        }
      }
    },
    [options, onRearrange],
  )

  const renderCenterEmpty = () => {
    let message = 'No matching options'
    if (onAddOption && options.length === 0) {
      message = 'No values added'
    }
    return (
      <span className="flex items-center justify-center rounded-lg border border-dashed border-border-normal py-1.5">
        {message}
      </span>
    )
  }

  const renderBottomEmpty = () => {
    if (!inputValue || !onAddOption) {
      return null
    } else {
      // TODO(ux): truncate if large, it doesn't wrap but still not ideal
      return (
        <Command.Empty>
          <Button
            type="button"
            onClick={handleAddOption}
            variant="primary"
            className="w-full"
            size="sm"
          >
            Add <span className="text-foreground-subtle">&quot;</span>
            {inputValue}
            <span className="text-foreground-subtle">&quot;</span>
          </Button>
        </Command.Empty>
      )
    }
  }

  const isSortable = !!onRearrange
  const hasValue = isMulti ? value.length > 0 : !!value
  const showInput = !!onAddOption // TODO: eventually filter options too

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          className={classes(
            'flex h-8 w-full items-center justify-between rounded-md border border-border-normal bg-transparent py-2 pl-3 pr-2 text-left outline-none ring-0 placeholder:text-foreground-subtle disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
            'outline-none focus:outline-2 focus:outline-offset-[-2px] focus:outline-focus-outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline',
            { 'text-foreground-subtle': !hasValue },
          )}
          style={{ width: width ? `${width}px` : undefined }}
        >
          <span
            className="truncate"
            style={{ width: width ? `${width - 16}px` : undefined }}
          >
            {renderSelected()}
          </span>
          <ChevronUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </Popover.Trigger>

      <Popover.Content
        className="w-full p-0"
        side="bottom"
        align="end"
        {...popover}
      >
        <Command>
          {showInput && (
            <>
              <Command.Input
                ref={inputRef}
                value={inputValue}
                onValueChange={setInputValue}
                placeholder="Enter value"
              />
              <Command.Empty>{renderCenterEmpty()}</Command.Empty>
            </>
          )}
          <Command.List>
            <Command.Group>
              {isSortable ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={options.map((opt) => opt.value)}
                    strategy={verticalListSortingStrategy}
                  >
                    {options.map((option) => (
                      <DraggableItem
                        key={option.value}
                        option={option}
                        handleChange={handleChange}
                        renderActiveDecoration={renderActiveDecoration}
                        isMulti={isMulti || false}
                        useSwitches={useSwitches || false}
                        value={value}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                options.map((option) => (
                  <Command.Item
                    key={option.value}
                    value={option.value}
                    onSelect={(value) => handleChange(value)}
                    className="justify-between"
                    disabled={option.disabled}
                  >
                    {option.label}
                    {renderActiveDecoration(option)}
                  </Command.Item>
                ))
              )}
            </Command.Group>
          </Command.List>

          {!!onAddOption && renderBottomEmpty()}
        </Command>
      </Popover.Content>
    </Popover>
  )
}

type DraggableItemProps = {
  option: SelectOption
  handleChange: (value: string) => void
  renderActiveDecoration: (option: SelectOption) => React.ReactNode
  isMulti: boolean
  useSwitches: boolean
  value: string | string[]
}

function DraggableItem({
  option,
  handleChange,
  renderActiveDecoration,
  isMulti,
  useSwitches,
  value,
}: DraggableItemProps) {
  const isActive = isMulti
    ? value.includes(option.value)
    : value === option.value
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: option.value,
    disabled: !isActive,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleSelect = (value: string) => {
    if (useSwitches) {
      return
    }
    handleChange(value)
  }

  return (
    <Command.Item
      ref={setNodeRef}
      style={style}
      {...attributes}
      value={option.value}
      onSelect={handleSelect}
      className={classes('justify-between', { 'opacity-50': isDragging })}
      disabled={option.disabled}
    >
      <span className="flex items-center gap-1">
        <button
          {...listeners}
          className={classes(
            isDragging ? 'z-10 cursor-grabbing' : 'cursor-grab',
            !isActive && 'cursor-default opacity-10',
          )}
        >
          <Drag className="h-4 w-4 text-foreground-subtle" />
        </button>
        {option.label}
      </span>
      {renderActiveDecoration(option)}
    </Command.Item>
  )
}
